import React, { useEffect, useState } from "react"
import axios from "axios"
import { Modal } from "reactstrap"

import { BASE_URL } from "../../../../global"
import type { AiSubject } from "./aiLessonPlanningTypes"

const SUBJECTS: Array<{ subject: AiSubject; id: number; label: string }> = [
  { subject: "math", id: 2, label: "Maths" },
  { subject: "english", id: 1, label: "English" },
]

interface MasteryActivity {
  masteryscore?: number | string | null
  fk_sub_id?: number | string | null
  question_attended?: number | string | null
  activity_node?: Array<Record<string, any>> | null
}

interface SubjectSnapshot {
  subject: AiSubject
  label: string
  passed: number
  inProgress: number
  weak: number
  notStarted: number
  total: number
  weakItems: Array<{ key: string; name: string; attempts: string; score: string }>
}

interface CheckNotice {
  text: string
}

function scoreOf(activity: MasteryActivity): number {
  const value = Number(activity.masteryscore)
  return Number.isFinite(value) ? value : 0
}

function activityName(activity: MasteryActivity): string {
  const node = Array.isArray(activity.activity_node) ? activity.activity_node[0] : null
  return (
    node?.node_name ||
    node?.topic_name ||
    node?.name ||
    node?.video_name ||
    node?.file_name ||
    "Activity"
  )
}

function buildSubject(
  subject: (typeof SUBJECTS)[number],
  activities: MasteryActivity[]
): SubjectSnapshot {
  const rows = activities.filter(activity => Number(activity.fk_sub_id) === subject.id)
  let passed = 0
  let inProgress = 0
  let weak = 0
  let notStarted = 0

  rows.forEach(activity => {
    const score = scoreOf(activity)
    if (score >= 70) passed += 1
    else if (score >= 30) inProgress += 1
    else if (score >= 1) weak += 1
    else notStarted += 1
  })

  const weakItems = rows
    .filter(activity => {
      const score = scoreOf(activity)
      return score >= 1 && score < 30
    })
    .sort((a, b) => scoreOf(a) - scoreOf(b))
    .slice(0, 4)
    .map((activity, index) => {
      const attended = Number(activity.question_attended)
      return {
        key: `${activityName(activity)}-${index}`,
        name: activityName(activity),
        attempts: Number.isFinite(attended) && attended > 0 ? `${attended} questions` : "—",
        score: scoreOf(activity).toFixed(1),
      }
    })

  return {
    subject: subject.subject,
    label: subject.label,
    passed,
    inProgress,
    weak,
    notStarted,
    total: rows.length,
    weakItems,
  }
}

function buildNotice(activities: MasteryActivity[]): CheckNotice | null {
  const flagged = activities
    .filter(activity => scoreOf(activity) === 0 && Number(activity.question_attended) >= 8)
    .sort(
      (a, b) => Number(b.question_attended) - Number(a.question_attended)
    )[0]

  if (!flagged) return null
  return {
    text: `${activityName(flagged)} was attempted across ${Number(
      flagged.question_attended
    )} questions in the last month and scored 0. The low score may reflect guessing rather than the skill.`,
  }
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  const letters = parts.slice(0, 2).map(part => part.charAt(0).toUpperCase())
  return letters.join("") || "?"
}

interface ChildSnapshotProps {
  isOpen: boolean
  onClose: () => void
  studentKey: string | null
  studentName: string
  yearLabel: string
  subjectLine: string
  pathBySubject: Partial<Record<AiSubject, string[]>>
}

export const ChildSnapshot = ({
  isOpen,
  onClose,
  studentKey,
  studentName,
  yearLabel,
  subjectLine,
  pathBySubject,
}: ChildSnapshotProps) => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [subjects, setSubjects] = useState<SubjectSnapshot[]>([])
  const [notice, setNotice] = useState<CheckNotice | null>(null)

  useEffect(() => {
    if (!isOpen || !studentKey) return
    const authUser = JSON.parse(localStorage.getItem("authUser") || "null")
    if (!authUser) {
      setError("Sign in again to load this student's scores.")
      return
    }

    let cancelled = false
    setLoading(true)
    setError(null)
    axios.defaults.headers.common["Authorization"] = `Bearer ${authUser}`
    axios
      .post(BASE_URL + "student/mastryScroe", { pk_student_key: studentKey })
      .then(response => {
        if (cancelled) return
        const activities: MasteryActivity[] =
          response.data?.studentDetails?.mistryScore || []
        setSubjects(SUBJECTS.map(subject => buildSubject(subject, activities)))
        setNotice(buildNotice(activities))
      })
      .catch(() => {
        if (cancelled) return
        setError("Could not load this student's recent scores.")
        setSubjects([])
        setNotice(null)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [isOpen, studentKey])

  return (
    <Modal
      isOpen={isOpen}
      toggle={onClose}
      size="lg"
      centered
      scrollable
      contentClassName="ai-lp-modal"
      aria-label="Child snapshot"
    >
      <div className="ai-lp-snap">
        <div className="ai-lp-snap-head">
          <div className="ai-lp-snap-identity">
            <div className="ai-lp-snap-avatar" aria-hidden="true">
              {initials(studentName)}
            </div>
            <div className="ai-lp-head-text">
              <span className="ai-lp-snap-name">{studentName || "Student"}</span>
              <span className="ai-lp-sub">
                {[yearLabel, subjectLine].filter(Boolean).join(" · ") || "—"}
              </span>
            </div>
          </div>
          <button
            type="button"
            className="ai-lp-icon-btn"
            aria-label="Close snapshot"
            onClick={onClose}
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#1B2240"
              strokeWidth="2.4"
              strokeLinecap="round"
              aria-hidden="true"
            >
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        </div>

        {loading ? (
          <div className="ai-lp-empty">
            <span className="spinner-border spinner-border-sm me-2" role="status" />
            Loading recent scores…
          </div>
        ) : null}

        {error ? <div className="ai-lp-alert">{error}</div> : null}

        {!loading && notice ? (
          <div className="ai-lp-snap-notice">
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#B05E00"
              strokeWidth="2"
              strokeLinecap="round"
              aria-hidden="true"
            >
              <path d="M12 3l9.5 17h-19z" />
              <path d="M12 10v4M12 17v.5" />
            </svg>
            <div>
              <b>Check before planning</b>
              <span>{notice.text}</span>
            </div>
          </div>
        ) : null}

        {!loading && !error ? (
          <div className="ai-lp-snap-grid">
            {subjects.map(subject => (
              <div className="ai-lp-snap-subject" key={subject.subject}>
                <div className="ai-lp-snap-subject-head">
                  <span>{subject.label}</span>
                  <span className="ai-lp-sub">
                    {yearLabel ? `${yearLabel} · ` : ""}
                    {subject.passed} of {subject.total} skills passed
                  </span>
                </div>
                <div className="ai-lp-snap-stats">
                  <div className="ai-lp-snap-stat ai-lp-snap-stat--passed">
                    <b>{subject.passed}</b>
                    <span>Passed</span>
                  </div>
                  <div className="ai-lp-snap-stat ai-lp-snap-stat--progress">
                    <b>{subject.inProgress}</b>
                    <span>In progress</span>
                  </div>
                  <div className="ai-lp-snap-stat ai-lp-snap-stat--weak">
                    <b>{subject.weak}</b>
                    <span>Weak</span>
                  </div>
                  <div className="ai-lp-snap-stat ai-lp-snap-stat--idle">
                    <b>{subject.notStarted}</b>
                    <span>Not started</span>
                  </div>
                </div>
                <span className="ai-lp-eyebrow">Weak skills</span>
                {subject.weakItems.length === 0 ? (
                  <span className="ai-lp-sub">No weak skills in the last month.</span>
                ) : (
                  subject.weakItems.map(item => (
                    <div className="ai-lp-snap-weak" key={item.key}>
                      <span className="ai-lp-snap-dot" />
                      <span className="ai-lp-snap-weak-name">{item.name}</span>
                      <span className="ai-lp-sub">{item.attempts}</span>
                      <span className="ai-lp-snap-score">{item.score}</span>
                    </div>
                  ))
                )}
              </div>
            ))}
          </div>
        ) : null}

        <div className="ai-lp-path">
          <span className="ai-lp-eyebrow">Learning path</span>
          {SUBJECTS.map(subject => {
            const steps = pathBySubject[subject.subject] || []
            return (
              <div className="ai-lp-path-row" key={subject.subject}>
                <span className="ai-lp-path-subject">{subject.label}</span>
                <span className="ai-lp-step ai-lp-step--now" title={steps[0]}>
                  <b>NOW</b>
                  {steps[0] || "—"}
                </span>
                <span className="ai-lp-arrow" aria-hidden="true">→</span>
                <span className="ai-lp-step" title={steps[1]}>
                  <b>NEXT</b>
                  {steps[1] || "—"}
                </span>
                <span className="ai-lp-arrow" aria-hidden="true">→</span>
                <span className="ai-lp-step" title={steps[2]}>
                  <b>THEN</b>
                  {steps[2] || "—"}
                </span>
              </div>
            )
          })}
        </div>

        {!loading ? (
          <span className="ai-lp-sub">Scores are from activities attempted in the last month.</span>
        ) : null}
      </div>
    </Modal>
  )
}

export const SnapshotIcon = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    aria-hidden="true"
  >
    <rect x="4" y="3" width="16" height="18" rx="2" />
    <path d="M8 15v2M12 11v6M16 8v9" />
  </svg>
)
